// src/Chat.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './App.css';

/**
 * One conversation, rendered inside an iframe on jousy.com.co/chats.
 *
 * Speaks to the same three backends as the floating widget on the main site:
 * history from the chat API, live traffic over the chat socket, image uploads
 * through the CDN pipeline. Feature parity with the widget is deliberate —
 * a photo sent from one UI must render identically in the other.
 */

const MESSAGES_API = 'https://messages.jousy.com.co/api/chat/get_messages';
const UPLOAD_API = 'https://api.jousy.com.co/api/cdn/uploadBase64';
const SOCKET_BASE = 'wss://socket.jousy.com.co';
// Images render as <img> in the other person's browser, so only pictures that
// went through Jousy's own CDN are shown — the same rule the socket relay and
// the chat API enforce server-side. A foreign URL stays visible as plain text.
const CDN_BASE = 'https://pub-4d3274fc68084a83ba5bc9fda57f11e4.r2.dev/';

// "14:32" today, "ayer 14:32" yesterday, "05/09/2026 14:32" beyond — inside a
// conversation the day usually IS today, so the common case stays short.
const formatTime = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  const hhmm = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  const today = new Date();
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(date, today)) return hhmm;
  const yesterday = new Date(today.getTime() - 86400000);
  if (sameDay(date, yesterday)) return `ayer ${hhmm}`;
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${hhmm}`;
};

const isCdnImageUrl = (value) =>
  typeof value === 'string' && value.startsWith(CDN_BASE);

/**
 * Habeas-data transparency (Ley 1581/2012): shared images live on a PUBLIC
 * CDN — anyone holding the link can view them. The standing note under the
 * input says so; this confirmation makes sure it was seen once, before the
 * first image ever leaves the device. If storage is unavailable (private
 * windows), it simply asks every time.
 */
const PUBLIC_MEDIA_ACK = 'jchat-public-media-ack';
const confirmPublicMedia = () => {
  try {
    if (localStorage.getItem(PUBLIC_MEDIA_ACK) === '1') return true;
  } catch {}
  const accepted = window.confirm(
    'Las imágenes que compartas en el chat se guardan en un servidor público: ' +
    'cualquier persona con el enlace podría verlas.\n\n' +
    'No envíes documentos de identidad, datos bancarios ni información sensible.\n\n' +
    '¿Quieres continuar?'
  );
  if (accepted) {
    try { localStorage.setItem(PUBLIC_MEDIA_ACK, '1'); } catch {}
  }
  return accepted;
};

const Chat = () => {
  const { id: currentUserId, chatid, token, fullname } = useParams();
  const [messages, setMessages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const ws = useRef(null);
  const endOfMessagesRef = useRef(null);
  const fileInputRef = useRef(null);
  const seenIdsRef = useRef(new Set()); // prevent duplicates
  const reconnectRef = useRef({ attempts: 0, timer: null, closed: false });

  // --- helpers ---------------------------------------------------
  const safeDate = (value) => {
    const d = value ? new Date(value) : new Date();
    return isNaN(d.getTime()) ? new Date() : d;
  };

  const normalizeMessage = useCallback((raw) => {
    const user =
      raw.userSenderId ??
      raw.user_sender_id ??
      raw.user ??
      raw.userId ??
      raw.senderId ??
      '';

    const content = raw.content ?? raw.message ?? '';
    // History rows carry createdAt; live frames carry the relay's sentAt.
    // Messages older than either field fall back to "now".
    const created =
      raw.createdAt ?? raw.created_at ?? raw.sentAt ?? raw.timestamp ?? null;

    const date = safeDate(created);

    // 'TEXT' | 'IMAGE'; anything else — including messages from before types
    // existed — is text.
    const messageType = raw.messageType === 'IMAGE' ? 'IMAGE' : 'TEXT';

    // Prefer stable IDs from server; otherwise synthesize one
    const id =
      raw.id ??
      raw._id ??
      `${String(user)}::${date.getTime()}::${content.length}`;

    return {
      id: String(id),
      user: String(user),
      message: content,
      messageType,
      timestamp: formatTime(date),
    };
  }, []);

  const appendIfNew = useCallback((raw) => {
    const msg = normalizeMessage(raw);
    if (seenIdsRef.current.has(msg.id)) return;
    seenIdsRef.current.add(msg.id);
    setMessages((prev) => [...prev, msg]);
  }, [normalizeMessage]);
  // ---------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    const state = reconnectRef.current;
    state.closed = false;

    const fetchMessages = async () => {
      try {
        const resp = await axios.post(
          MESSAGES_API,
          { chatId: chatid },
          {
            headers: {
              accept: 'application/json',
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!cancelled && resp?.data?.success && Array.isArray(resp.data.data)) {
          const normalized = resp.data.data.map(normalizeMessage);
          // seed the seenIds set to avoid WS duplicates of history
          normalized.forEach((m) => seenIdsRef.current.add(m.id));
          setMessages(normalized);
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
      }
    };

    fetchMessages();

    // The socket drops on deploys, sleep, flaky mobile networks. Reconnecting
    // with capped backoff means a person who left the tab open still receives
    // messages an hour later, instead of a silently dead conversation.
    const connect = () => {
      if (cancelled || state.closed) return;
      const socket = new WebSocket(`${SOCKET_BASE}/ws/${chatid}/${currentUserId}`);
      ws.current = socket;

      socket.onopen = () => {
        state.attempts = 0;
      };

      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          appendIfNew(parsed);
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };

      socket.onclose = () => {
        if (cancelled || state.closed) return;
        state.attempts += 1;
        const delay = Math.min(1000 * state.attempts, 10000);
        state.timer = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // onclose follows and owns the retry.
      };
    };

    connect();

    return () => {
      cancelled = true;
      state.closed = true;
      if (state.timer) clearTimeout(state.timer);
      try {
        ws.current?.close();
      } catch {}
    };
    // Reconnect if chat/user/token changes
  }, [currentUserId, chatid, token, normalizeMessage, appendIfNew]);

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendPayload = (content, messageType) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      // DO NOT optimistically append: the relay echoes the sender's own
      // message back to the room, and one code path renders both sides.
      ws.current.send(JSON.stringify({
        token,
        message: content,
        messageType,
        user: currentUserId,
      }));
      return true;
    }
    console.warn('WebSocket not open; message not sent');
    return false;
  };

  const sendMessage = () => {
    const inputEl = document.getElementById('chat-input');
    const text = (inputEl?.value ?? '').trim();
    if (!text) return;
    if (sendPayload(text, 'TEXT') && inputEl) inputEl.value = '';
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') sendMessage();
  };

  // --- images ----------------------------------------------------
  // A 12 MP phone photo is ~6 MB the recipient does not need: downscaled to
  // 1280px JPEG before upload, then the CDN URL travels as an IMAGE message.
  const downscale = (file, maxSide, quality) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl.replace(/^data:image\/jpeg;base64,/, ''));
        } catch (e) {
          reject(e);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('No es una imagen válida'));
      };
      img.src = url;
    });

  const onPickImage = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const base64 = await downscale(file, 1280, 0.85);
      const resp = await axios.post(
        UPLOAD_API,
        {
          filename: `chat-${Date.now()}.jpg`,
          size: Math.floor(base64.length * 0.75),
          mimetype: 'image/jpeg',
          base64,
          title: 'chat',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const key = resp?.data?.data?.resized;
      if (resp?.data?.success && key) {
        sendPayload(`${CDN_BASE}${key}`, 'IMAGE');
      } else {
        alert('No pudimos subir la imagen. Intenta de nuevo.');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('No pudimos enviar la imagen. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  };
  // ---------------------------------------------------------------

  const initial = (fullname || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-header-avatar">{initial}</div>
        <div className="chat-header-info">
          <div className="chat-header-name">{fullname}</div>
          <div className="chat-header-sub">Conversación activa</div>
        </div>
      </div>
      <div className="chat-window">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`message ${
              String(msg.user) === String(currentUserId)
                ? 'message-right'
                : 'message-left'
            }`}
          >
            {msg.messageType === 'IMAGE' && isCdnImageUrl(msg.message) ? (
              <div className="message-text message-image">
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img
                  src={msg.message}
                  alt="Imagen"
                  loading="lazy"
                  onClick={() => window.open(msg.message, '_blank', 'noopener')}
                />
              </div>
            ) : (
              <div className="message-text">{msg.message}</div>
            )}
            <div className="message-timestamp">{msg.timestamp}</div>
          </div>
        ))}
        <div ref={endOfMessagesRef} />
      </div>

      {uploading && (
        <div className="chat-uploading">Enviando imagen…</div>
      )}

      <div className="chat-public-note">
        ⚠️ Las imágenes compartidas en el chat son públicas para cualquier persona
        con el enlace. Evita enviar documentos o datos sensibles.
      </div>

      <div className="chat-input-container">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={onPickImage}
        />
        <button
          className="chat-image-btn"
          title="Enviar imagen (se comparte de forma pública)"
          disabled={uploading}
          onClick={() => { if (confirmPublicMedia()) fileInputRef.current?.click(); }}
        >
          📷
        </button>
        <input
          id="chat-input"
          type="text"
          onKeyDown={onKeyDown}
          placeholder="Escribe un mensaje..."
        />
        <button className="chat-send-btn" title="Enviar" onClick={sendMessage}>➤</button>
      </div>
    </div>
  );
};

export default Chat;
